import { FC, useCallback, useState } from 'react'

import moment from 'moment-timezone'

import { Path } from 'constants/router'
import { WorkspaceActionPopup } from 'components/pages/Workspace/WorkspaceActionPopup'
import { useDeleteWorkspace } from 'dataQueries'
import { useQueryClient } from '@tanstack/react-query'
import { EntityKeys } from 'utils/reactQuery'
import { logger } from 'utils/logging'
import { useNavigate } from 'react-router'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'

interface Props {
  /** Whether the modal is open */
  isOpen: boolean
  /** Callback to close the modal and reset state */
  onClose: () => void
}

export const DeleteWorkspacePopup: FC<Props> = ({
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation(['workspace_settings', 'common'])
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { spawnSuccessToast, spawnErrorToast } = useSnackbar()
  const { currentUserWorkspace, userWorkspacesQuery } = useUserData()
  const deleteWorkspace = useDeleteWorkspace()

  // deleteWorkspace.isPending drops before the refetch settles, which would re-enable the confirm button mid-flight
  const [isFinalizing, setIsFinalizing] = useState(false)

  const workspaceName = currentUserWorkspace?.name || ''
  const workspaceId = currentUserWorkspace?.id || ''

  const handleClose = useCallback(() => {
    deleteWorkspace.reset()
    onClose()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- .reset is stable
  }, [deleteWorkspace.reset, onClose])

  const handleConfirm = useCallback(() => {
    setIsFinalizing(true)
    deleteWorkspace.mutate({ workspaceId }, {
      onSuccess: async () => {
        // react-query calls onSuccess fire-and-forget: a throw past the first await would surface nowhere
        try {
          queryClient.invalidateQueries({ queryKey: [EntityKeys.USER_ME] })

          // A period-end removal keeps the workspace listed until the end date, while `terminatedAt` can lag the
          // 204 (the backend learns of the cancellation asynchronously) — so listing membership decides the
          // outcome, not the date. A failed refetch resolves with the pre-delete list instead of rejecting and
          // lands in the same arm, the one that keeps the client's data reachable.
          const refreshed = await userWorkspacesQuery.refetch()
          const scheduledWorkspace = refreshed.data?.workspaces
            ?.find((workspace) => workspace.workspaceId === workspaceId)

          if (scheduledWorkspace || refreshed.isError) {
            if (refreshed.isError) {
              logger.error('Failed to refresh workspaces after deletion', refreshed.error, { context: { component: 'DeleteWorkspacePopup', action: 'refetchWorkspaces', metadata: { workspaceId } } })
            }
            const terminatedAt = scheduledWorkspace?.terminatedAt
            const endDate = terminatedAt ? moment(terminatedAt) : undefined
            spawnSuccessToast(endDate?.isValid()
              ? t('workspace_settings:danger_zone.delete_workspace_scheduled_success', { date: endDate.format('DD.MM.YYYY') })
              : t('workspace_settings:danger_zone.delete_workspace_scheduled_no_date'))
            handleClose()
            return
          }

          // Deleting a workspace is a soft cancel — the row survives with a CANCELED status — so the
          // remembered id is NOT nulled by the FK and still names this workspace. Resolution skips it
          // regardless, because it only ever selects workspaces with an active subscription status.
          sessionStorage.removeItem('workspaceId')
          spawnSuccessToast(t('workspace_settings:danger_zone.delete_workspace_success'))
          navigate(Path.INDEX)
          handleClose()
        } catch (error) {
          logger.error('Failed to finalize workspace deletion', error, { context: { component: 'DeleteWorkspacePopup', action: 'deleteWorkspaceSuccess', metadata: { workspaceId } } })
          spawnErrorToast(t('workspace_settings:danger_zone.delete_workspace_error'))
          handleClose()
        } finally {
          setIsFinalizing(false)
        }
      },
      onError: (error) => {
        logger.error('Failed to delete workspace', error, { context: { component: 'DeleteWorkspacePopup', action: 'deleteWorkspace', metadata: { workspaceId } } })
        spawnErrorToast(t('workspace_settings:danger_zone.delete_workspace_error'))
        deleteWorkspace.reset()
        setIsFinalizing(false)
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- .mutate and .reset are stable
  }, [deleteWorkspace.mutate, deleteWorkspace.reset, workspaceId, spawnSuccessToast, spawnErrorToast, t, queryClient, navigate, handleClose, userWorkspacesQuery.refetch])

  return (
    <WorkspaceActionPopup
      isOpen={isOpen}
      workspaceName={workspaceName}
      title={t('workspace_settings:danger_zone.delete_workspace_title', { workspaceName })}
      warningTitle={t('workspace_settings:danger_zone.delete_workspace_warning_title')}
      warningDescription={t('workspace_settings:danger_zone.delete_workspace_warning_description')}
      inputLabel={t('workspace_settings:danger_zone.delete_workspace_input_label')}
      isPending={deleteWorkspace.isPending || isFinalizing}
      onClose={handleClose}
      onConfirm={handleConfirm}
    />
  )
}
